// src/pages/LectureTerms.js
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PageContainer from "../components/Layout/PageContainer";
import SeoHead from "../components/SeoHead";
import { useLanguage } from "../i18n/LanguageContext";
import LectureFaqMenu from "../components/LectureFaqMenu";
import "../styles/lectureUpload.css";

function detectUiLocale(navUploadLabel) {
  if (!navUploadLabel) return "en";
  if (navUploadLabel.includes("上传")) return "zh-Hans";
  if (/[\u4e00-\u9fff]/.test(navUploadLabel)) return "zh-Hant";
  return "en";
}

const LectureTerms = () => {
  const { t } = useLanguage();
  const navUploadLabel = t ? t("navUpload") : "Upload";
  const uiLocale = detectUiLocale(navUploadLabel);

  const strings = useMemo(() => {
    const updated = "Last updated: 02 Dec 2025 (Beta)";

    return {
      /* ------------------------------------------------------------
       *                    English Version
       * ------------------------------------------------------------ */
      en: {
        headTitle: "STT Lecture Service — Terms of Service (Beta)",
        headDesc:
          "Terms of Service for Lecture STT: accounts, uploads, content rules, usage limits, data retention, processing queue, disclaimers, and allowed usage.",
        pageTitle: "Terms of Service (TOS)",
        lastUpdated: updated,

        // Left menu
        faqMenuTitle: "STT FAQ Menu",
        faqMenuHowToUse: "How to Use",
        faqMenuTips: "Recording & Upload Tips",
        faqMenuLanguage: "Supported Languages",
        faqMenuPrivacy: "Privacy & Data",
        faqMenuVersion: "Version & Updates",
        faqMenuSupport: 'Support / Contact',
        faqMenuAll: "View all STT FAQs",

        s1: "1. Agreement",
        p1: "By accessing or using the Lecture STT Service (“Service”), you agree to these Terms. If you do not agree, please stop using the Service.",

        s2: "2. Service Summary",
        p2: "The Service allows you to upload or record audio (via Chrome extension) and generate transcripts and optional summaries. The Service is currently in Beta and may have limits, delays, or changes.",

        s3: "3. Accounts & Sign-in",
        l3: [
          "You must sign in with a supported login provider (e.g., Google). We do not store your third-party password.",
          "You are responsible for actions taken using your account.",
          "You must be at least 13 years old; if under 18, you need consent from a parent or guardian.",
        ],

        s4: "4. Uploads, Recordings & Ownership",
        l4: [
          "You confirm you have the rights and permissions needed to upload the audio and allow us and our subprocessors to process it for transcription and summary generation.",
          "You retain ownership of your audio files, transcripts, and summaries.",
          "We do not use your audio or transcripts to train AI models unless explicitly allowed by the Privacy Policy or you provide consent.",
          "You are responsible for ensuring your uploads do not violate laws or rights of others.",
        ],

        s5: "5. Acceptable Use",
        p5: "You agree NOT to upload, request, or generate content that:",
        l5: [
          "Is illegal, harmful, hateful, abusive, violent, deceptive, or adult content.",
          "Violates others’ intellectual property, privacy, confidentiality, or contractual obligations.",
          "Contains highly sensitive personal data unrelated to legitimate lecture/meeting use.",
          "Attempts to bypass processing limits, queue systems, verification, or security measures.",
          "Uses automated high-volume uploads, scraping, or attempts to overload the system.",
          "Is used for high-risk or professional decision-making areas (medical, legal, emergency, etc.).",
        ],

        s6: "6. Processing, Queue & Limits",
        l6: [
          "The Service may set limits such as file size, duration, concurrency, daily processing count, or queue priority.",
          "Processing time varies and is not guaranteed. Large files or peak usage may cause delays.",
          "We may pause or cancel jobs if they cause system instability or violate content rules.",
        ],

        s7: "7. Data Retention & Deletion",
        l7: [
          "Audio files are retained up to 30 days for redownload, unless you delete the lecture entry sooner.",
          "Transcripts and summaries are retained until you delete them from your Status page.",
          "Metadata (timestamps, job logs) may be kept for up to 30–90 days depending on purpose.",
          "You can delete lecture entries at any time, and files will be removed within a reasonable time.",
        ],

        s8: "8. Outputs",
        p8: "You own the transcripts and summaries generated by the Service, to the extent allowed by law. You are responsible for verifying accuracy and suitability before relying on them.",

        s9: "9. Disclaimers & Accuracy",
        l9: [
          "The Service is provided on an “AS IS” and “AS AVAILABLE” basis.",
          "We do not guarantee accuracy, completeness, or suitability of transcripts or summaries.",
          "AI/STT systems may mishear, omit, or incorrectly transcribe content.",
          "You must verify results before using them for study, work, or decision making.",
        ],

        s10: "10. Availability & Changes",
        l10: [
          "The Service may experience downtime for maintenance, upgrades, or events outside our control.",
          "Features, limits, and access may change as the Service evolves.",
          "These Terms may be updated with notice. Continued use constitutes acceptance.",
        ],

        s11: "11. Liability",
        l11: [
          "To the maximum extent permitted by law, we are not liable for indirect, incidental, special, punitive, or consequential damages, including loss of data or use.",
          "We are not responsible for decisions made based on transcripts or summaries.",
          "Your sole remedy for dissatisfaction is to stop using the Service.",
        ],

        s12: "12. Termination",
        p12: "If we reasonably believe you are violating these Terms or posing security/operational risks, we may suspend or limit your access. You may request deletion via Support / Contact.",

        s13: "13. Contact",
        p13: (
          <>
            Questions about the Terms? Contact us via{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              Support / Contact
            </Link>
            .
          </>
        ),
      },

      /* ------------------------------------------------------------
       *                    Traditional Chinese
       * ------------------------------------------------------------ */
      "zh-Hant": {
        headTitle: "STT 語音轉筆記 — 服務條款（Beta）",
        headDesc:
          "STT 語音轉筆記服務條款：上傳與錄音、帳號、使用規範、內容權利、處理與排隊限制、資料保存、免責聲明與終止使用。",
        pageTitle: "服務條款（Terms of Service）",
        lastUpdated: "最後更新：2025 年 12 月 2 日（Beta）",

        faqMenuTitle: "STT 常見問題選單",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "錄音與上傳小貼士",
        faqMenuLanguage: "支援語言",
        faqMenuPrivacy: "隱私與資料安全",
        faqMenuVersion: "版本與更新記錄",
        faqMenuSupport: '支援／聯繫我們',
        faqMenuAll: "查看全部 STT 常見問題",

        s1: "1. 同意條款",
        p1: "當你存取或使用 STT 語音轉筆記服務，即表示你同意本條款；若不同意，請勿使用。",

        s2: "2. 服務簡介",
        p2: "本服務可讓你上傳或錄製音訊，並產生逐字稿與摘要。目前為 Beta 階段，可能有使用限制、延遲或功能調整。",

        s3: "3. 帳號與登入",
        l3: [
          "需以支援的第三方帳號登入（如 Google）。我們不會保存你的第三方密碼。",
          "你應妥善保管帳號與使用行為。",
          "需年滿 13 歲；未滿 18 歲需經監護人同意。",
        ],

        s4: "4. 上傳、錄音與內容權利",
        l4: [
          "你確認已取得必要權利與授權，以供我們與受託處理者為提供服務之目的處理音訊。",
          "你保有音訊、逐字稿與摘要的權利。",
          "除非另行載明或經你同意，我們不會使用你的音訊或逐字稿訓練模型。",
          "你需確保上傳內容未違法或侵害他人權利。",
        ],

        s5: "5. 使用規範",
        p5: "你同意不得上傳、請求或產生以下內容：",
        l5: [
          "違法、有害、騷擾、仇恨、成人、暴力或其他不當內容。",
          "侵害第三方的智慧財產、隱私或保密義務。",
          "含高度敏感個資且與講課／會議無關的內容。",
          "試圖繞過處理上限、排隊機制、驗證或安全措施。",
          "使用自動化大量請求、爬蟲或使系統過載之行為。",
          "用於醫療、法律、緊急等高風險用途。",
        ],

        s6: "6. 處理時間、排隊與限制",
        l6: [
          "服務可能設定或調整上傳大小、音訊長度、每日次數、同時處理數或排隊優先權。",
          "處理時間不保證；大型檔案或尖峰時段可能延遲。",
          "若內容違規或影響系統穩定，可能被暫停或取消。",
        ],

        s7: "7. 資料保存與刪除",
        l7: [
          "音訊檔最多保留 30 天供重新下載，或你提前刪除。",
          "逐字稿／摘要會保存至你在狀態頁刪除講座為止。",
          "中繼資料（時間、紀錄）可能保存 30～90 天。",
          "你可隨時刪除講座，系統會於合理時間內移除檔案。",
        ],

        s8: "8. 輸出結果（Outputs）",
        p8: "你可以在合法範圍內使用產生的逐字稿與摘要。但你需自行確認其準確性，不得依此作高風險決策。",

        s9: "9. 免責聲明與準確度",
        l9: [
          "本服務以「現狀」與「可得」方式提供，不附帶任何明示或默示保證。",
          "不保證逐字稿與摘要的準確性、完整性或適用性。",
          "語音辨識可能誤聽、遺漏或轉錄錯誤。",
          "使用前請自行查核內容。",
        ],

        s10: "10. 可用性與變更",
        l10: [
          "服務可能因維護、升級或不可抗力而暫停。",
          "功能、限制與使用方式可能調整。",
          "條款更新後，續用視為同意。",
        ],

        s11: "11. 責任限制",
        l11: [
          "在法律允許範圍內，我們不對任何間接、附帶、懲罰性或後果性損害負責。",
          "我們不對你依據逐字稿與摘要所做決策負責。",
          "若你對服務不滿意，你的唯一救濟為停止使用。",
        ],

        s12: "12. 終止",
        p12: "若我們合理認定你違反條款或危及系統／他人安全，得限制、暫停或終止你的使用。你可透過支援頁請求刪除帳號或資料。",

        s13: "13. 聯絡我們",
        p13: (
          <>
            如對本條款有疑問，請至{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              支援／聯絡我們
            </Link>
            。
          </>
        ),
      },

      /* ------------------------------------------------------------
       *                    Simplified Chinese
       * ------------------------------------------------------------ */
      "zh-Hans": {
        headTitle: "STT 语音转笔记 — 服务条款（Beta）",
        headDesc:
          "STT 语音转笔记服务条款：上传与录音、账号、使用规范、内容权利、处理与排队、数据保存、免责与终止。",
        pageTitle: "服务条款（Terms of Service）",
        lastUpdated: "最后更新：2025 年 12 月 2 日（Beta）",

        faqMenuTitle: "STT 常见问题菜单",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "录音与上传小贴士",
        faqMenuLanguage: "支持语言",
        faqMenuPrivacy: "隐私与数据安全",
        faqMenuVersion: "版本与更新记录",
        faqMenuSupport: '客户／联系我们',
        faqMenuAll: "查看全部 STT 常见问题",

        s1: "1. 同意条款",
        p1: "当你使用 STT 语音转笔记服务，即表示你同意本条款。",

        s2: "2. 服务简介",
        p2: "你可以上传或录制音频，并生成逐字稿与摘要。目前为 Beta，可能有延迟或限制。",

        s3: "3. 账号与登录",
        l3: [
          "需使用第三方账号登录（如 Google）。我们不保存密码。",
          "你需对账号安全负责。",
          "需年满 13 岁；未满 18 岁需监护人同意。",
        ],

        s4: "4. 上传、录音与内容权利",
        l4: [
          "你确认拥有必要权利上传音频并允许我们处理。",
          "你保留音频、逐字稿与摘要的权利。",
          "除非隐私政策另有说明或获得同意，我们不会将音频或逐字稿用于训练模型。",
          "你需确保上传内容不违法、不侵权。",
        ],

        s5: "5. 使用规范",
        p5: "你不得上传或生成以下内容：",
        l5: [
          "违法、有害、暴力、仇恨、成人等内容。",
          "侵犯知识产权、隐私、保密义务的内容。",
          "包含高度敏感个人信息的内容。",
          "绕过限制、队列、验证或安全措施。",
          "自动化大量上传或使系统过载的行为。",
          "用于医疗、法律、紧急等高风险用途。",
        ],

        s6: "6. 处理与排队",
        l6: [
          "服务可能设置上传大小、长度、每日次数或同时处理限制。",
          "处理时间不保证；可能排队。",
          "违规或影响系统的任务可能被暂停或取消。",
        ],

        s7: "7. 数据保存",
        l7: [
          "音频文件最多保留 30 天，或由你手动删除。",
          "逐字稿与摘要保存至你在状态页删除。",
          "元数据保存 30～90 天。",
        ],

        s8: "8. 输出结果",
        p8: "逐字稿与摘要的权利属于你，但需自行确认其准确性。",

        s9: "9. 免责与准确度",
        l9: [
          "服务以“现状”“可用”方式提供，不保证无误。",
          "不保证逐字稿／摘要的准确性。",
          "STT 可能误听或遗漏内容。",
        ],

        s10: "10. 可用性与变更",
        l10: [
          "服务可能因维护或不可抗力暂停。",
          "功能与限制可能调整。",
          "条款更新后继续使用即视为同意。",
        ],

        s11: "11. 责任限制",
        l11: [
          "我们不对任何间接、附带或后果性损害负责。",
          "不对你依赖逐字稿作出的决定负责。",
        ],

        s12: "12. 终止",
        p12: "若你违反条款或影响系统安全，我们可暂停或终止使用。",

        s13: "13. 联系我们",
        p13: (
          <>
            如有疑问，请通过{" "}
            <Link to="/lecture/faq/support" className="text-blue-600 underline">
              客服／联系我们
            </Link>
            。
          </>
        ),
      },
    }[uiLocale];
  }, [uiLocale]);

  const url = "https://stt.aittsservice.online/lecture/faq/terms";

  return (
    <PageContainer>
      <SeoHead
        title={strings.headTitle}
        description={strings.headDesc}
        url={url}
      />

      {/* Hero */}
      <div className="card upload-hero">
      <div className="card-heading">
        <h1 className="card-title">{strings.pageTitle}</h1>
      </div>
        <p style={{ fontSize: 12, marginTop: 4 }}>{strings.lastUpdated}</p>
      </div>

      {/* Layout */}
      <div className="card upload-card upload-faq-card">
        <div className="upload-layout upload-layout--faq howto-grid">
          {/* Left menu */}
          <LectureFaqMenu active="terms" />


          {/* Right side content */}
          <main className="upload-main upload-main--faq howto-main">
            <section className="faq-section">
              <h2>{strings.s1}</h2>
              <p>{strings.p1}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s2}</h2>
              <p>{strings.p2}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s3}</h2>
              <ul>{strings.l3.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s4}</h2>
              <ul>{strings.l4.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s5}</h2>
              <p>{strings.p5}</p>
              <ul>{strings.l5.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s6}</h2>
              <ul>{strings.l6.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s7}</h2>
              <ul>{strings.l7.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s8}</h2>
              <p>{strings.p8}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s9}</h2>
              <ul>{strings.l9.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s10}</h2>
              <ul>{strings.l10.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s11}</h2>
              <ul>{strings.l11.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s12}</h2>
              <p>{strings.p12}</p>
            </section>

            <section className="faq-section">
              <h2>{strings.s13}</h2>
              <p>{strings.p13}</p>
            </section>
          </main>
        </div>
      </div>
    </PageContainer>
  );
};

export default LectureTerms;
